import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { externalShareHeaders } from "@/lib/externalShareToken";
import { consumeFreeQuota, FREE_QUOTA_MESSAGE } from "@/lib/plan/freeTier";


/** Max längd per inspelning (sekunder) – längre diktering görs i flera tagningar. */
const MAX_SECONDS = 120;
const TARGET_RATE = 16000;

const encodeWav = (chunks: Float32Array[], sampleRate: number): Blob => {
  const total = chunks.reduce((sum, c) => sum + c.length, 0);
  const merged = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }

  // Enkel nedsampling till 16 kHz för mindre uppladdning
  const ratio = Math.max(1, Math.round(sampleRate / TARGET_RATE));
  const outLength = Math.floor(merged.length / ratio);
  const out = new Int16Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const sample = Math.max(-1, Math.min(1, merged[i * ratio]));
    out[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  const outRate = Math.round(sampleRate / ratio);

  const buffer = new ArrayBuffer(44 + out.length * 2);
  const view = new DataView(buffer);
  const writeString = (pos: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(pos + i, str.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + out.length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, outRate, true);
  view.setUint32(28, outRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, out.length * 2, true);
  new Int16Array(buffer, 44).set(out);

  return new Blob([buffer], { type: "audio/wav" });
};

interface Recording {
  stream: MediaStream;
  ctx: AudioContext;
  source: MediaStreamAudioSourceNode;
  node: ScriptProcessorNode;
  chunks: Float32Array[];
  timer: number;
}

/**
 * Diktering: spelar in via mikrofonen och skickar en komplett WAV-fil till
 * transkriberingsfunktionen. Ljudet lagras aldrig – bara texten används.
 */
export const useVoiceDictation = (onText: (text: string) => void) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const recRef = useRef<Recording | null>(null);

  const isSupported =
    typeof window !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    !!(window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);

  const cleanup = useCallback((): Recording | null => {
    const rec = recRef.current;
    recRef.current = null;
    if (!rec) return null;
    window.clearTimeout(rec.timer);
    rec.stream.getTracks().forEach((t) => t.stop());
    try {
      rec.node.disconnect();
      rec.source.disconnect();
    } catch {
      /* redan frånkopplad */
    }
    return rec;
  }, []);

  const transcribe = useCallback(
    async (blob: Blob) => {
      setIsTranscribing(true);
      try {
        const quota = await consumeFreeQuota("ai");
        if (!quota.allowed) {
          toast.error("AI-kvoten är slut", { description: FREE_QUOTA_MESSAGE.ai });
          return;
        }
        const form = new FormData();

        form.append("file", blob, "inspelning.wav");
        const { data, error } = await supabase.functions.invoke("ai-transcribe-audio", {
          body: form,
          headers: externalShareHeaders(),
        });

        if (error) {
          let message = "Kunde inte tolka inspelningen";
          const context = (error as { context?: Response }).context;
          if (context && typeof context.text === "function") {
            const raw = await context.text().catch(() => "");
            try {
              message = JSON.parse(raw)?.error ?? message;
            } catch {
              /* behåll standardmeddelandet */
            }
          }
          toast.error(message);
          return;
        }

        const text = String((data as { text?: string })?.text ?? "").trim();
        if (!text) {
          toast.error("Ingen text hittades i inspelningen");
          return;
        }
        onText(text);
        toast.success("Texten är inlagd");
      } catch (e) {
        toast.error("Kunde inte tolka inspelningen", {
          description: (e as Error).message,
        });
      } finally {
        setIsTranscribing(false);
      }
    },
    [onText],
  );

  const stop = useCallback(async () => {
    const rec = cleanup();
    setIsRecording(false);
    if (!rec) return;

    const blob = encodeWav(rec.chunks, rec.ctx.sampleRate);
    await rec.ctx.close().catch(() => undefined);

    if (blob.size < 4096) {
      toast.error("Inspelningen var för kort – försök igen.");
      return;
    }
    await transcribe(blob);
  }, [cleanup, transcribe]);

  const start = useCallback(async () => {
    if (!isSupported) {
      toast.error("Din webbläsare stödjer inte inspelning");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Mikrofonen behöver godkännas", {
        description: "Tillåt mikrofon i webbläsaren och försök igen.",
      });
      return;
    }

    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    const source = ctx.createMediaStreamSource(stream);
    const node = ctx.createScriptProcessor(4096, 1, 1);
    const chunks: Float32Array[] = [];
    node.onaudioprocess = (e) => chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
    source.connect(node);
    node.connect(ctx.destination);

    const timer = window.setTimeout(() => {
      toast.info("Inspelningen stoppades efter 2 minuter", {
        description: "Fortsätt gärna i en ny tagning – texten läggs till efter den förra.",
      });
      void stop();
    }, MAX_SECONDS * 1000);

    recRef.current = { stream, ctx, source, node, chunks, timer };
    setIsRecording(true);
  }, [isSupported, stop]);

  const toggle = useCallback(() => {
    if (isRecording) void stop();
    else void start();
  }, [isRecording, start, stop]);

  return { isSupported, isRecording, isTranscribing, start, stop, toggle };
};

export default useVoiceDictation;
